import React, { ReactNode, useState, useCallback, useEffect, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import { FieldVisibilityContext, FieldVisibility, SectionName } from './contexts';
import { extractApiErrorMessage } from '../utils/errorUtils';
import {
  getFieldVisibility as getFieldVisibilityApi,
  updateFieldVisibility as updateFieldVisibilityApi,
  bulkUpdateFieldVisibility as bulkUpdateFieldVisibilityApi,
  makeSectionPublic as makeSectionPublicApi,
  makeSectionPrivate as makeSectionPrivateApi,
  makeObjectPublic as makeObjectPublicApi,
  makeObjectPrivate as makeObjectPrivateApi,
  SECTION_FIELDS,
  ALWAYS_PRIVATE_FIELDS,
  isSectionPublic as checkSectionPublic,
  isSectionPrivate as checkSectionPrivate,
  isObjectFieldsPublic as checkObjectPublic,
  isObjectFieldsPrivate as checkObjectPrivate,
} from '../utils/fieldVisibilityApi';
import { logger } from '../utils/logger';

/**
 * Extracts a user-friendly error message from various error types.
 * Handles AxiosError specifically to extract server response details.
 */
const getErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const axiosError = err as AxiosError<{ detail?: unknown; message?: string }>;
    // Try to get server error message from response — handles both string and array detail
    if (axiosError.response?.data?.detail !== undefined) {
      return extractApiErrorMessage(axiosError.response.data, axiosError.response.status ?? 0);
    }
    const serverMessage = axiosError.response?.data?.message;
    if (serverMessage) return serverMessage;
    // Fall back to status text
    if (axiosError.response?.statusText) return axiosError.response.statusText;
  }
  if (err instanceof Error) return err.message;
  return fallback;
};

interface FieldVisibilityProviderProps {
  children: ReactNode;
  catalogItemId: string;
  ownerId?: string | null;
}

/**
 * Provider for field-level visibility/privacy controls.
 * Implements optimistic updates with rollback on error.
 */
const FieldVisibilityProvider: React.FC<FieldVisibilityProviderProps> = ({
  children,
  catalogItemId,
  ownerId,
}) => {
  const [fieldSettings, setFieldSettings] = useState<Record<string, FieldVisibility>>({});
  const [loading, setLoading] = useState(false);
  // isLoading starts true and becomes false after the initial fetch completes.
  // This prevents the UI from briefly showing "Mixed" before settings are loaded.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track if initial fetch has been done for this catalogItemId
  const fetchedForRef = useRef<string | null>(null);

  // Fetch field visibility settings on mount or when catalogItemId changes
  useEffect(() => {
    if (!catalogItemId) return;

    // Skip if we've already fetched for this catalogItemId
    if (fetchedForRef.current === catalogItemId) return;
    fetchedForRef.current = catalogItemId;

    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getFieldVisibilityApi(catalogItemId, ownerId);
        setFieldSettings(response.fieldSettings || {});
      } catch (err) {
        const errorMessage = getErrorMessage(err, 'Failed to fetch field visibility settings');
        setError(errorMessage);
        logger.error('[FieldVisibility] Error fetching settings', err);
      } finally {
        setLoading(false);
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [catalogItemId, ownerId]);

  const refreshFieldVisibility = useCallback(async () => {
    if (!catalogItemId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getFieldVisibilityApi(catalogItemId, ownerId);
      setFieldSettings(response.fieldSettings || {});
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to fetch field visibility settings');
      setError(errorMessage);
      logger.error('[FieldVisibility] Error fetching settings', err);
    } finally {
      setLoading(false);
    }
  }, [catalogItemId, ownerId]);

  const getFieldVisibility = useCallback(
    (fieldPath: string): FieldVisibility => {
      const explicit = fieldSettings[fieldPath];
      if (explicit) return explicit;
      // Per-row paths (e.g. provenance.exhibitions.{rowId}) inherit their
      // parent section's visibility when no explicit per-row entry exists.
      // Mirrors backend _should_include_row: rows are public when the
      // section is public. Without this fallback, "Make all public" flips
      // section paths but the row chips keep showing PRIVATE because the
      // per-row keys were never written.
      const parts = fieldPath.split('.');
      if (parts.length > 2) {
        const sectionPath = `${parts[0]}.${parts[1]}`;
        return fieldSettings[sectionPath] || 'private';
      }
      return 'private';
    },
    [fieldSettings]
  );

  const updateFieldVisibility = useCallback(
    async (fieldPath: string, visibility: FieldVisibility) => {
      if (!catalogItemId) return;

      // Optimistic update
      const previousSettings = { ...fieldSettings };
      setFieldSettings((prev) => ({
        ...prev,
        [fieldPath]: visibility,
      }));

      try {
        setError(null);
        const response = await updateFieldVisibilityApi(catalogItemId, fieldPath, visibility, ownerId);
        // Update with server response
        setFieldSettings(response.fieldSettings || {});
      } catch (err) {
        // Rollback on error
        setFieldSettings(previousSettings);
        const errorMessage = getErrorMessage(err, 'Failed to update field visibility');
        setError(errorMessage);
        logger.error('[FieldVisibility] Error updating field', err);
        // Note: Error is reflected in state, not thrown - callers check error state
      }
    },
    [catalogItemId, fieldSettings]
  );

  const bulkUpdateFieldVisibility = useCallback(
    async (fields: Array<{ fieldPath: string; visibility: FieldVisibility }>) => {
      if (!catalogItemId) return;

      // Optimistic update
      const previousSettings = { ...fieldSettings };
      const optimisticUpdate = fields.reduce(
        (acc, { fieldPath, visibility }) => ({
          ...acc,
          [fieldPath]: visibility,
        }),
        { ...fieldSettings }
      );
      setFieldSettings(optimisticUpdate);

      try {
        setError(null);
        const response = await bulkUpdateFieldVisibilityApi(catalogItemId, fields, ownerId);
        // Update with server response
        setFieldSettings(response.fieldSettings || {});
      } catch (err) {
        // Rollback on error
        setFieldSettings(previousSettings);
        const errorMessage = getErrorMessage(err, 'Failed to bulk update field visibility');
        setError(errorMessage);
        logger.error('[FieldVisibility] Error bulk updating fields', err);
        // Note: Error is reflected in state, not thrown - callers check error state
      }
    },
    [catalogItemId, fieldSettings]
  );

  const makeSectionPublic = useCallback(
    async (section: SectionName) => {
      if (!catalogItemId) return;

      // Optimistic update - set section fields to public, skipping always-private fields
      const previousSettings = { ...fieldSettings };
      const sectionFields = SECTION_FIELDS[section];
      const optimisticUpdate = sectionFields
        .filter((fieldPath) => !ALWAYS_PRIVATE_FIELDS.has(fieldPath))
        .reduce(
          (acc, fieldPath) => ({
            ...acc,
            [fieldPath]: 'public' as FieldVisibility,
          }),
          { ...fieldSettings }
        );
      setFieldSettings(optimisticUpdate);

      try {
        setError(null);
        const response = await makeSectionPublicApi(catalogItemId, section, ownerId);
        // Update with server response
        setFieldSettings(response.fieldSettings || {});
      } catch (err) {
        // Rollback on error
        setFieldSettings(previousSettings);
        const errorMessage = getErrorMessage(err, 'Failed to make section public');
        setError(errorMessage);
        logger.error('[FieldVisibility] Error making section public', err);
        // Note: Error is reflected in state, not thrown - callers check error state
      }
    },
    [catalogItemId, fieldSettings]
  );

  const makeSectionPrivate = useCallback(
    async (section: SectionName) => {
      if (!catalogItemId) return;

      // Optimistic update - set all section fields to private
      const previousSettings = { ...fieldSettings };
      const sectionFields = SECTION_FIELDS[section];
      const optimisticUpdate = sectionFields.reduce(
        (acc, fieldPath) => ({
          ...acc,
          [fieldPath]: 'private' as FieldVisibility,
        }),
        { ...fieldSettings }
      );
      setFieldSettings(optimisticUpdate);

      try {
        setError(null);
        const response = await makeSectionPrivateApi(catalogItemId, section, ownerId);
        // Update with server response
        setFieldSettings(response.fieldSettings || {});
      } catch (err) {
        // Rollback on error
        setFieldSettings(previousSettings);
        const errorMessage = getErrorMessage(err, 'Failed to make section private');
        setError(errorMessage);
        logger.error('[FieldVisibility] Error making section private', err);
        // Note: Error is reflected in state, not thrown - callers check error state
      }
    },
    [catalogItemId, fieldSettings]
  );

  const isSectionPublic = useCallback(
    (section: SectionName): boolean => {
      return checkSectionPublic(fieldSettings, section);
    },
    [fieldSettings]
  );

  const isSectionPrivate = useCallback(
    (section: SectionName): boolean => {
      return checkSectionPrivate(fieldSettings, section);
    },
    [fieldSettings]
  );

  const makeObjectPublic = useCallback(
    async () => {
      if (!catalogItemId) return;

      const previousSettings = { ...fieldSettings };
      const allSections: SectionName[] = ['describe', 'media', 'measure', 'catalogKeywords', 'provenance', 'additionalInfo', 'upload'];
      const optimisticUpdate = allSections.reduce(
        (acc, section) => {
          const sectionFields = SECTION_FIELDS[section];
          return sectionFields
            .filter((fieldPath) => !ALWAYS_PRIVATE_FIELDS.has(fieldPath))
            .reduce((innerAcc, fieldPath) => ({ ...innerAcc, [fieldPath]: 'public' as FieldVisibility }), acc);
        },
        { ...fieldSettings }
      );
      setFieldSettings(optimisticUpdate);

      try {
        setError(null);
        const response = await makeObjectPublicApi(catalogItemId, ownerId);
        setFieldSettings(response.fieldSettings || {});
      } catch (err) {
        setFieldSettings(previousSettings);
        const errorMessage = getErrorMessage(err, 'Failed to make object public');
        setError(errorMessage);
        logger.error('[FieldVisibility] Error making object public', err);
      }
    },
    [catalogItemId, fieldSettings, ownerId]
  );

  const makeObjectPrivate = useCallback(
    async () => {
      if (!catalogItemId) return;

      const previousSettings = { ...fieldSettings };
      const allSections: SectionName[] = ['describe', 'media', 'measure', 'catalogKeywords', 'provenance', 'additionalInfo', 'upload'];
      const optimisticUpdate = allSections.reduce(
        (acc, section) => {
          const sectionFields = SECTION_FIELDS[section];
          return sectionFields.reduce(
            (innerAcc, fieldPath) => ({ ...innerAcc, [fieldPath]: 'private' as FieldVisibility }), acc
          );
        },
        { ...fieldSettings }
      );
      setFieldSettings(optimisticUpdate);

      try {
        setError(null);
        const response = await makeObjectPrivateApi(catalogItemId, ownerId);
        setFieldSettings(response.fieldSettings || {});
      } catch (err) {
        setFieldSettings(previousSettings);
        const errorMessage = getErrorMessage(err, 'Failed to make object private');
        setError(errorMessage);
        logger.error('[FieldVisibility] Error making object private', err);
      }
    },
    [catalogItemId, fieldSettings, ownerId]
  );

  const isObjectPublic = useCallback(
    (): boolean => checkObjectPublic(fieldSettings),
    [fieldSettings]
  );

  const isObjectPrivate = useCallback(
    (): boolean => checkObjectPrivate(fieldSettings),
    [fieldSettings]
  );

  return (
    <FieldVisibilityContext.Provider
      value={{
        fieldSettings,
        loading,
        isLoading,
        error,
        getFieldVisibility,
        updateFieldVisibility,
        bulkUpdateFieldVisibility,
        makeSectionPublic,
        makeSectionPrivate,
        isSectionPublic,
        isSectionPrivate,
        makeObjectPublic,
        makeObjectPrivate,
        isObjectPublic,
        isObjectPrivate,
        refreshFieldVisibility,
      }}
    >
      {children}
    </FieldVisibilityContext.Provider>
  );
};

// Note: useFieldVisibility hook is in hooks/useFieldVisibility.ts for fast refresh compatibility

export { FieldVisibilityProvider };
